/**
 * Quick capture input.
 *
 * Single-line input wired to POST /api/v1/tasks. Smart defaults
 * apply server-side. Submitting clears the field and the new task
 * appears in its right column via TanStack Query invalidation.
 *
 * Used on the home view (M10) and per-column on the board (M4). The
 * per-column variant passes the column's status as a hint so the
 * created task lands where the user clicked.
 *
 * Reference: spec section 5.1 (smart defaults) and 15.4.
 */

import { useState, type FormEvent } from 'react';
import type { TaskStatus } from '@workspace/shared';

import { useCreateTask } from '@/lib/api/tasks';
import { todayIso } from '@/lib/utils';

interface QuickCaptureProps {
  /** Pre-fills the new task's status. The smart-default cascade may
   * still override based on due-date inference; we leave that to the
   * server. */
  status?: TaskStatus;
  placeholder?: string;
  /** Called after the task is successfully created. The parent uses
   * this for closing a modal or scrolling to the new task. */
  onCreated?: (taskId: string) => void;
}

export function QuickCapture({ status, placeholder, onCreated }: QuickCaptureProps) {
  const [value, setValue] = useState('');
  const createTask = useCreateTask();

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const title = value.trim();
    if (!title || createTask.isPending) return;
    try {
      // PR #35: quick-capture defaults `dueDate` to today so the backend
      // smart-default cascade routes the new task into DOING (when WIP
      // has space) or TODAY rather than parking it on BACKLOG.
      // Peter wants capture-then-act, not capture-then-triage.
      const created = await createTask.mutateAsync({ title, status, dueDate: todayIso() });
      setValue('');
      onCreated?.(created.id);
    } catch {
      // The mutation hook surfaces errors via TanStack Query's
      // status; the UI surfaces them via the disabled-state below.
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex h-9 w-full items-center border border-mx-grey-800 bg-mx-grey-900 px-mx-3"
    >
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder ?? 'Capture a task. Press Enter to save.'}
        aria-label="Quick capture"
        className="flex-1 bg-transparent text-mx-body-sm text-mx-white placeholder:text-mx-grey-500 focus:outline-none"
      />
      <button
        type="submit"
        disabled={!value.trim() || createTask.isPending}
        className="mono-label text-mx-orange disabled:text-mx-grey-mid"
      >
        {createTask.isPending ? 'SAVING…' : 'ADD'}
      </button>
    </form>
  );
}
